import { createContext, useContext, useState, useEffect } from 'react';
import { crearEquipo, getEquipos, eliminarEquipo } from '../services/api';
import { useAuth } from './AuthContext';

const EquipoContext = createContext();

export function EquipoProvider({ children }) {
  const { token, user } = useAuth();
  const [equipos,       setEquipos]       = useState([]);
  const [equipo,        setEquipo]        = useState(null);
  const [deporteActivo, setDeporteActivo] = useState('futbol');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  useEffect(() => {
    if (!user || !token) { setEquipo(null); setEquipos([]); return; }
    getEquipos().then(data => {
      if (Array.isArray(data)) {
        const misEquipos = data.filter(e => e.capitan_id === user.id);
        setEquipos(misEquipos);

        if (misEquipos.length > 0) {
          // Si viene del login con deporte específico, usar ese
          const deporteLogin = localStorage.getItem('turival_deporte_login_pendiente');
          if (deporteLogin) {
            localStorage.removeItem('turival_deporte_login_pendiente');
            setDeporteActivo(deporteLogin);
            activarEquipoDeDeporte(misEquipos, deporteLogin);
          } else {
            // Usar el deporte del primer equipo de fútbol, o el primero que tenga
            const equipoFutbol = misEquipos.find(e => !e.deporte || e.deporte === 'futbol');
            const deporteInicial = equipoFutbol
              ? 'futbol'
              : (misEquipos[0]?.deporte || 'futbol');
            setDeporteActivo(deporteInicial);
            activarEquipoDeDeporte(misEquipos, deporteInicial);
          }
        }
      }
    }).catch(() => {});
  }, [user, token]);

  // Cuando cambia el deporte, cambiar el equipo activo
  useEffect(() => {
    if (equipos.length > 0) {
      activarEquipoDeDeporte(equipos, deporteActivo);
    }
  }, [deporteActivo]);

  const activarEquipoDeDeporte = (lista, deporte) => {
    const esFutbol = deporte === 'futbol';
    const match = lista.find(e =>
      esFutbol
        ? (!e.deporte || e.deporte === 'futbol')
        : e.deporte === deporte
    );
    if (match) {
      setEquipo({
        ...match,
        colorPrincipal:  match.color_principal,
        colorSecundario: match.color_secundario,
      });
    } else {
      setEquipo(null);
    }
  };

  const cambiarDeporte = (deporte) => {
    setDeporteActivo(deporte);
    // Guardar solo cuando viene del login para que AuthCallback lo use
    localStorage.setItem('turival_deporte_login_pendiente', deporte);
  };

  const cambiarEquipo = (id) => {
    const e = equipos.find(eq => eq.id === id);
    if (e) setEquipo({
      ...e,
      colorPrincipal:  e.color_principal,
      colorSecundario: e.color_secundario,
    });
  };

  const registrarEquipo = async (payload, tokenExterno) => {
    setLoading(true);
    setError(null);
    try {
      const t    = tokenExterno || token;
      const data = await crearEquipo(payload, t);
      if (data.error) { setError(data.error); return false; }
      const nuevoEquipo = {
        ...data,
        colorPrincipal:  data.color_principal,
        colorSecundario: data.color_secundario,
      };
      setEquipos(prev => [...prev, nuevoEquipo]);
      setEquipo(nuevoEquipo);
      if (data.deporte) setDeporteActivo(data.deporte);
      return true;
    } catch {
      setError('Error de conexión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const borrarEquipo = async (id) => {
    try {
      const data = await eliminarEquipo(id, token);
      if (data.error) return { ok: false, error: data.error };
      const nuevosEquipos = equipos.filter(e => e.id !== id);
      setEquipos(nuevosEquipos);
      if (equipo?.id === id) {
        activarEquipoDeDeporte(nuevosEquipos, deporteActivo);
      }
      return { ok: true };
    } catch {
      return { ok: false, error: 'Error de conexión' };
    }
  };

  const actualizarEquipoEnContexto = (data) => {
    const equipoActualizado = {
      ...data,
      colorPrincipal:  data.color_principal,
      colorSecundario: data.color_secundario,
    };
    setEquipos(prev => prev.map(e => e.id === data.id ? equipoActualizado : e));
    if (equipo?.id === data.id) setEquipo(equipoActualizado);
  };

  return (
    <EquipoContext.Provider value={{
      equipo, setEquipo, equipos,
      deporteActivo, cambiarDeporte,
      cambiarEquipo, registrarEquipo, borrarEquipo,
      actualizarEquipoEnContexto,
      loading, error,
    }}>
      {children}
    </EquipoContext.Provider>
  );
}

export function useEquipo() {
  return useContext(EquipoContext);
}
